import axios from 'axios';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { getUrlParamsFetch, handleAxiosError } from '@/shared';
import { erpApi } from '@/shared/axios/erp-api';
import { urlBackend } from '@/shared/constants';
import { useAuthStore } from '@/store/auth/auth.store';
import {
  AlertSeverityEnum,
  UseMutationsParams,
} from '@/shared/interfaces';
import {
  ITicket,
  ITicketCreate,
  ITicketPagingRes,
  ITicketResumen,
  ITicketHistorial,
  ITicketArchivo,
  ITacketResp,
} from '@/shared/interfaces/erp/tickets-soporte-clientes';
import { useUiStore } from '@/store/ui';

import {
  USE_MOCK,
  MOCK_TICKETS_PAGING,
  MOCK_RESUMEN,
  mockAxiosResponse,
} from './tickets-mock-data';

const { get, post, put, patch, remove } = erpApi();

const BASE_URL = `${urlBackend}api/v1/gestion-tickets/`;

///* tanStack query

export type GetGestionTicketsParams = Partial<ITicket> & {
  page?: number;
  page_size?: number;
  estado?: string;
  prioridad?: string;
  area?: string;
  usuario_asignado?: string;
  cliente_codigo?: string;
  enabled?: boolean;
};

export type UpdateGestionTicketParams = {
  id: string;
  data: ITicketCreate;
};

export type EscalarTicketParams = {
  id: string;
  data: { area_destino: string; comentario: string };
};

export type AsignarTicketParams = {
  id: string;
  data: { usuario_asignado_id: number; usuario_asignado_nombre: string };
};

export type CerrarTicketParams = {
  id: string;
  // El backend acepta el arbol nuevo (cierre_categoria + cierre_subcategoria)
  // o el campo legacy (subcategoria_cierre). El frontend dejo de usar el
  // legacy en el ModalCerrar pero el tipo lo mantiene opcional por compat
  // con cualquier consumer no migrado y con el endpoint multipart antiguo.
  data: {
    solucion: string;
    cierre_categoria?: string;
    cierre_subcategoria?: string;
    subcategoria_cierre?: string;
  };
};

export type AgregarComentarioParams = {
  id: string;
  data: { comentario: string };
};

export type GetTicketResumenParams = {
  area?: string;
  usuario_asignado?: string;
  enabled?: boolean;
};

export const useFetchGestionTickets = ({
  page = 1,
  page_size = 10,
  enabled = true,
  ...params
}: GetGestionTicketsParams) => {
  return useQuery({
    queryKey: ['gestion-tickets', page, page_size, params],
    queryFn: async () => {
      if (USE_MOCK) return mockAxiosResponse(MOCK_TICKETS_PAGING);
      return getGestionTickets({ page, page_size, ...params });
    },
    enabled,
  });
};

export const useGetGestionTicket = (id: string) => {
  return useQuery({
    queryKey: ['gestion-tickets', id],
    queryFn: async () => {
      /* if (USE_MOCK) {
        const ticket = MOCK_TICKETS.find((t) => t.id_ticket === id) || MOCK_TICKETS[0];
        return mockAxiosResponse(ticket);
      } */
      return getGestionTicket(id);
    },
  });
};

export const useCreateGestionTicket = ({
  navigate,
  enableRedirect = true,
  returnUrl,
  returnErrorUrl,
  enableErrorRedirect = false,
  customMessageErrorAlert,
  enableSuccessAlert = true,
  customSuccessMessage,
}: UseMutationsParams) => {
  const queryClient = useQueryClient();
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: createGestionTicket,
    onSuccess: () => {
      // refetchQueries con type:'active' refetchea SI Y SOLO SI la query
      // esta montada en pantalla (lista de mis-tickets, dashboard, etc.).
      // invalidateQueries por si solo marca stale pero a veces no dispara
      // refetch por race conditions de re-render — el listado se queda con
      // data vieja hasta F5. refetchQueries fuerza el GET inmediatamente.
      // El prefijo ['gestion-tickets'] cubre tambien
      // ['gestion-tickets','resumen',...] (badges del header) porque
      // refetchQueries con queryKey prefijo matchea todas las queries cuyas
      // keys empiezan con ese array.
      queryClient.refetchQueries({ queryKey: ['gestion-tickets'], type: 'active' });
      // Vision 360 del cliente: el tab "Tickets" lista los tickets del
      // cliente. Sin invalidar aqui, despues de crear el ticket el panel
      // mostraba data vieja hasta hacer F5 manual.
      queryClient.refetchQueries({ queryKey: ['vision-360'], type: 'active' });
      enableRedirect && navigate && returnUrl && navigate(returnUrl);
      enableSuccessAlert &&
        setAlert &&
        setAlert(true, customSuccessMessage ?? 'Ticket creado correctamente', AlertSeverityEnum.success);
    },
    onError: error => {
      enableErrorRedirect && navigate && returnUrl && navigate(returnErrorUrl ?? (returnUrl || ''));
      handleAxiosError(error, customMessageErrorAlert);
    },
    onSettled: () => { setIsGlobalLoading(false); },
  });
};

export const useUpdateGestionTicket = ({
  navigate,
  enableRedirect = true,
  returnUrl,
  returnErrorUrl,
  enableErrorRedirect = false,
  customMessageErrorAlert,
  enableSuccessAlert = true,
  customSuccessMessage,
}: UseMutationsParams) => {
  const queryClient = useQueryClient();
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: updateGestionTicket,
    onSuccess: () => {
      // refetchQueries con type:'active' refetchea SI Y SOLO SI la query
      // esta montada en pantalla (lista de mis-tickets, dashboard, etc.).
      // invalidateQueries por si solo marca stale pero a veces no dispara
      // refetch por race conditions de re-render — el listado se queda con
      // data vieja hasta F5. refetchQueries fuerza el GET inmediatamente.
      queryClient.refetchQueries({ queryKey: ['gestion-tickets'], type: 'active' });
      enableRedirect && navigate && returnUrl && navigate(returnUrl);
      enableSuccessAlert &&
        setAlert &&
        setAlert(true, customSuccessMessage ?? 'Ticket actualizado correctamente', AlertSeverityEnum.success);
    },
    onError: error => {
      enableErrorRedirect && navigate && returnUrl && navigate(returnErrorUrl ?? (returnUrl || ''));
      handleAxiosError(error, customMessageErrorAlert);
    },
    onSettled: () => { setIsGlobalLoading(false); },
  });
};

export const useEscalarTicket = () => {
  const queryClient = useQueryClient();
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: escalarTicket,
    onSuccess: () => {
      // refetchQueries con type:'active' refetchea SI Y SOLO SI la query
      // esta montada en pantalla (lista de mis-tickets, dashboard, etc.).
      // invalidateQueries por si solo marca stale pero a veces no dispara
      // refetch por race conditions de re-render — el listado se queda con
      // data vieja hasta F5. refetchQueries fuerza el GET inmediatamente.
      queryClient.refetchQueries({ queryKey: ['gestion-tickets'], type: 'active' });
      setAlert && setAlert(true, 'Ticket escalado correctamente', AlertSeverityEnum.success);
    },
    onError: error => { handleAxiosError(error); },
    onSettled: () => { setIsGlobalLoading(false); },
  });
};

export const useAsignarTicket = () => {
  const queryClient = useQueryClient();
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: asignarTicket,
    onSuccess: () => {
      // refetchQueries con type:'active' refetchea SI Y SOLO SI la query
      // esta montada en pantalla (lista de mis-tickets, dashboard, etc.).
      // invalidateQueries por si solo marca stale pero a veces no dispara
      // refetch por race conditions de re-render — el listado se queda con
      // data vieja hasta F5. refetchQueries fuerza el GET inmediatamente.
      queryClient.refetchQueries({ queryKey: ['gestion-tickets'], type: 'active' });
      setAlert && setAlert(true, 'Ticket asignado correctamente', AlertSeverityEnum.success);
    },
    onError: error => { handleAxiosError(error); },
    onSettled: () => { setIsGlobalLoading(false); },
  });
};

export const useCerrarTicket = () => {
  const queryClient = useQueryClient();
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: cerrarTicket,
    onSuccess: () => {
      // refetchQueries con type:'active' refetchea SI Y SOLO SI la query
      // esta montada en pantalla (lista de mis-tickets, dashboard, etc.).
      // invalidateQueries por si solo marca stale pero a veces no dispara
      // refetch por race conditions de re-render — el listado se queda con
      // data vieja hasta F5. refetchQueries fuerza el GET inmediatamente.
      queryClient.refetchQueries({ queryKey: ['gestion-tickets'], type: 'active' });
      setAlert && setAlert(true, 'Ticket cerrado correctamente', AlertSeverityEnum.success);
    },
    onError: error => { handleAxiosError(error); },
    onSettled: () => { setIsGlobalLoading(false); },
  });
};

export const useAgregarComentario = () => {
  const queryClient = useQueryClient();
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: agregarComentario,
    // Refetch explicito del historial: invalidateQueries solo marca como
    // stale y refetch si la query esta "active", lo cual puede fallar en
    // race conditions con re-renders. refetchQueries garantiza la
    // actualizacion sin tener que esperar F5 del usuario.
    onSuccess: async (_data, vars) => {
      await Promise.all([
        queryClient.refetchQueries({ queryKey: ['gestion-tickets', 'historial', vars.id] }),
        queryClient.invalidateQueries({ queryKey: ['gestion-tickets', vars.id] }),
        // Refetch del listado activo: la fila del ticket comentado debe
        // mostrar la nueva fecha_modificacion / ultimo comentario sin F5.
        queryClient.refetchQueries({ queryKey: ['gestion-tickets'], type: 'active' }),
      ]);
      setAlert && setAlert(true, 'Comentario agregado correctamente', AlertSeverityEnum.success);
    },
    onError: error => { handleAxiosError(error); },
    onSettled: () => { setIsGlobalLoading(false); },
  });
};

export const useFetchTicketHistorial = (ticketId: string) => {
  return useQuery({
    queryKey: ['gestion-tickets', 'historial', ticketId],
    queryFn: async () => {
      /* if (USE_MOCK) {
        const filtered = MOCK_HISTORIAL.filter((h) => h.ticket === ticketId);
        return mockAxiosResponse(filtered.length > 0 ? filtered : MOCK_HISTORIAL);
      } */
      return getTicketHistorial(ticketId);
    },
    enabled: !!ticketId,
  });
};

export const useFetchTicketResumen = ({
  enabled = true,
  ...params
}: GetTicketResumenParams) => {
  return useQuery({
    queryKey: ['gestion-tickets', 'resumen', params],
    queryFn: async () => {
      if (USE_MOCK) return mockAxiosResponse(MOCK_RESUMEN);
      return getTicketResumen(params);
    },
    enabled,
  });
};

///* axios
export const getGestionTickets = async (params?: GetGestionTicketsParams) => {
  const queryParams = getUrlParamsFetch({ ...params });
  return get<ITicketPagingRes>(`${BASE_URL}tickets/?${queryParams}`, true);
};

export const getGestionTicket = async (id: string) => {
  try {
    return await get<ITacketResp>(`${BASE_URL}tickets/${id}/`, true);
  } catch (error) {
    return handleAxiosError(error);
  }
};

export const createGestionTicket = async (data: ITicketCreate) => {
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  setIsGlobalLoading(true);
  return post<ITicket>(`${BASE_URL}tickets/`, data, true);
};

export const updateGestionTicket = async ({ id, data }: UpdateGestionTicketParams) => {
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  setIsGlobalLoading(true);
  return put<ITicket>(`${BASE_URL}tickets/${id}/`, data, true);
};

export const escalarTicket = async ({ id, data }: EscalarTicketParams) => {
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  setIsGlobalLoading(true);
  return patch<ITicket>(`${BASE_URL}tickets/${id}/escalar/`, data, true);
};

export const asignarTicket = async ({ id, data }: AsignarTicketParams) => {
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  setIsGlobalLoading(true);
  return patch<ITicket>(`${BASE_URL}tickets/${id}/asignar/`, data, true);
};

export const cerrarTicket = async ({ id, data }: CerrarTicketParams) => {
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  setIsGlobalLoading(true);
  return patch<ITicket>(`${BASE_URL}tickets/${id}/cerrar/`, data, true);
};

export const reabrirTicket = async (id: string) => {
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  setIsGlobalLoading(true);
  return patch<ITicket>(`${BASE_URL}tickets/${id}/cambiar-estado/`, { nuevo_estado_codigo: 'abierto' }, true);
};

export const useReabrirTicket = () => {
  const queryClient = useQueryClient();
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: reabrirTicket,
    onSuccess: () => {
      // refetchQueries con type:'active' refetchea SI Y SOLO SI la query
      // esta montada en pantalla (lista de mis-tickets, dashboard, etc.).
      // invalidateQueries por si solo marca stale pero a veces no dispara
      // refetch por race conditions de re-render — el listado se queda con
      // data vieja hasta F5. refetchQueries fuerza el GET inmediatamente.
      queryClient.refetchQueries({ queryKey: ['gestion-tickets'], type: 'active' });
      queryClient.invalidateQueries({ queryKey: ['gestion-ticket'] });
      setAlert && setAlert(true, 'Ticket reabierto correctamente', AlertSeverityEnum.success);
    },
    onError: error => { handleAxiosError(error); },
    onSettled: () => { setIsGlobalLoading(false); },
  });
};

/**
 * Hook generico para cambiar el estado del ticket via codigo (en_proceso,
 * pendiente_cliente, resuelto, etc.). NO se usa para 'cerrado' (eso pasa
 * por el ModalCerrar que requiere solucion + categoria/subcategoria).
 */
export const useCambiarEstadoTicket = () => {
  const queryClient = useQueryClient();
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: async ({ id, codigo }: { id: string; codigo: string }) => {
      setIsGlobalLoading(true);
      return patch<ITicket>(
        `${BASE_URL}tickets/${id}/cambiar-estado/`,
        { nuevo_estado_codigo: codigo },
        true,
      );
    },
    onSuccess: () => {
      queryClient.refetchQueries({ queryKey: ['gestion-tickets'], type: 'active' });
      queryClient.invalidateQueries({ queryKey: ['gestion-ticket'] });
      setAlert && setAlert(true, 'Estado actualizado', AlertSeverityEnum.success);
    },
    onError: error => { handleAxiosError(error); },
    onSettled: () => { setIsGlobalLoading(false); },
  });
};

export const agregarComentario = async ({ id, data }: AgregarComentarioParams) => {
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  setIsGlobalLoading(true);
  return post<ITicket>(`${BASE_URL}tickets/${id}/comentario/`, data, true);
};

// --- Adjuntos en comentarios ---

export type AgregarComentarioConArchivosParams = {
  id: string;
  comentario: string;
  archivos: File[];
};

/**
 * Comentario + N archivos en un solo request multipart. Si el comentario
 * llega vacio el backend lo deja como entry "archivo" en el historial.
 *
 * Usa axios directo (no el wrapper erpApi.post) porque el wrapper alterna
 * responseType=blob cuando typeJson=false, y aqui queremos JSON.
 */
export const agregarComentarioConArchivos = async (
  { id, comentario, archivos }: AgregarComentarioConArchivosParams,
) => {
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  setIsGlobalLoading(true);
  const fd = new FormData();
  if (comentario && comentario.trim().length > 0) {
    // Se envia como Blob para que axios lo pase como part de texto y no
    // como un campo url-encoded — asi llega al backend como String del
    // @RequestPart("comentario").
    fd.append('comentario', new Blob([comentario], { type: 'text/plain' }));
  }
  archivos.forEach(f => fd.append('archivos', f));
  const token = useAuthStore.getState().token;
  const resp = await axios.post(
    `${BASE_URL}tickets/${id}/comentario-con-archivos`,
    fd,
    {
      headers: { Authorization: 'Token ' + token },
      timeout: 600000,
    },
  );
  return resp.data as { historial: ITicketHistorial; archivos: ITicketArchivo[] };
};

export const useAgregarComentarioConArchivos = () => {
  const queryClient = useQueryClient();
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: agregarComentarioConArchivos,
    // refetchQueries fuerza el refetch sin depender de que la query este
    // "active": el TimelineHistorial monta useFetchTicketArchivos en
    // paralelo, asi que sin un refetch explicito la UI no muestra los
    // adjuntos hasta el siguiente window-focus o un F5.
    onSuccess: async (_data, vars) => {
      await Promise.all([
        queryClient.refetchQueries({ queryKey: ['gestion-tickets', 'historial', vars.id] }),
        queryClient.refetchQueries({ queryKey: ['gestion-tickets', 'archivos', vars.id] }),
        queryClient.invalidateQueries({ queryKey: ['gestion-tickets', vars.id] }),
        // Lista de mis-tickets / dashboard: nueva fecha_modificacion al instante.
        queryClient.refetchQueries({ queryKey: ['gestion-tickets'], type: 'active' }),
      ]);
      setAlert && setAlert(true, 'Comentario y archivos agregados correctamente', AlertSeverityEnum.success);
    },
    onError: error => { handleAxiosError(error); },
    onSettled: () => { setIsGlobalLoading(false); },
  });
};

export const getTicketArchivos = async (ticketId: string) => {
  return get<ITicketArchivo[]>(`${BASE_URL}tickets/${ticketId}/archivos/`, true);
};

// --- Cerrar ticket con archivos ---

export type CerrarTicketConArchivosParams = {
  id: string;
  solucion: string;
  /** Modelo viejo (compat). El nuevo flujo usa cierreCategoria + cierreSubcategoria. */
  subcategoriaCierre?: string | null;
  /** V1_19_0: categoria del arbol nuevo de cierre. */
  cierreCategoria?: string | null;
  /** V1_19_0: subcategoria del arbol nuevo de cierre. */
  cierreSubcategoria?: string | null;
  archivos: File[];
};

/**
 * Cierra un ticket via multipart aceptando solucion + subcategoria_cierre
 * + N archivos. Si la lista de archivos esta vacia el flujo es equivalente
 * al endpoint legacy /cerrar pero pasando por el mismo handler multipart.
 */
export const cerrarTicketConArchivos = async (
  { id, solucion, subcategoriaCierre, cierreCategoria, cierreSubcategoria, archivos }: CerrarTicketConArchivosParams,
) => {
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  setIsGlobalLoading(true);
  const fd = new FormData();
  fd.append('solucion', new Blob([solucion], { type: 'text/plain' }));
  if (subcategoriaCierre) {
    fd.append('subcategoria_cierre', new Blob([subcategoriaCierre], { type: 'text/plain' }));
  }
  if (cierreCategoria) {
    fd.append('cierre_categoria', new Blob([cierreCategoria], { type: 'text/plain' }));
  }
  if (cierreSubcategoria) {
    fd.append('cierre_subcategoria', new Blob([cierreSubcategoria], { type: 'text/plain' }));
  }
  archivos.forEach(f => fd.append('archivos', f));
  const token = useAuthStore.getState().token;
  const resp = await axios.post(
    `${BASE_URL}tickets/${id}/cerrar-con-archivos`,
    fd,
    {
      headers: { Authorization: 'Token ' + token },
      timeout: 600000,
    },
  );
  return resp.data;
};

export const useCerrarTicketConArchivos = () => {
  const queryClient = useQueryClient();
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: cerrarTicketConArchivos,
    onSuccess: async (_data, vars) => {
      await Promise.all([
        queryClient.refetchQueries({ queryKey: ['gestion-tickets', 'historial', vars.id] }),
        queryClient.refetchQueries({ queryKey: ['gestion-tickets', 'archivos', vars.id] }),
        queryClient.invalidateQueries({ queryKey: ['gestion-tickets', vars.id] }),
        queryClient.invalidateQueries({ queryKey: ['gestion-tickets'] }),
      ]);
      setAlert && setAlert(true, 'Ticket cerrado correctamente', AlertSeverityEnum.success);
    },
    onError: error => { handleAxiosError(error); },
    onSettled: () => { setIsGlobalLoading(false); },
  });
};

export const useFetchTicketArchivos = (ticketId: string) => {
  return useQuery({
    queryKey: ['gestion-tickets', 'archivos', ticketId],
    queryFn: () => getTicketArchivos(ticketId),
    enabled: !!ticketId,
  });
};

/**
 * Pide al backend la URL firmada de MinIO (15 min) para el archivo dado y
 * abre la descarga en una nueva pestaña. NO se puede usar el endpoint con
 * un anchor <a href>: el browser no envia el header Authorization en esa
 * navegacion y el gateway responde 401. La firma de MinIO autentica la
 * descarga sin necesitar el token.
 */
export const descargarArchivo = async (archivoId: string) => {
  try {
    const resp = await get<{ url: string; nombre_original?: string; content_type?: string }>(
      `${BASE_URL}archivos/${archivoId}/download`,
      true,
    );
    const url = (resp as any)?.data?.url;
    if (!url) {
      throw new Error('Respuesta sin URL de descarga');
    }
    window.open(url, '_blank', 'noopener,noreferrer');
  } catch (error) {
    handleAxiosError(error);
  }
};

/**
 * Devuelve la URL firmada de MinIO del archivo (misma que usa descargarArchivo)
 * SIN abrirla, para previsualizar imagenes en un <img> dentro de un modal.
 * La firma autentica el acceso, asi que el <img src> carga sin token.
 */
export const obtenerUrlArchivo = async (archivoId: string): Promise<string | null> => {
  try {
    const resp = await get<{ url: string }>(
      `${BASE_URL}archivos/${archivoId}/download`,
      true,
    );
    return (resp as any)?.data?.url ?? null;
  } catch (error) {
    handleAxiosError(error);
    return null;
  }
};

export const getTicketHistorial = async (ticketId: string) => {
  try {
    return await get<ITicketHistorial[]>(`${BASE_URL}tickets/${ticketId}/historial/`, true);
  } catch (error) {
    return handleAxiosError(error);
  }
};

export const getTicketResumen = async (params?: Omit<GetTicketResumenParams, 'enabled'>) => {
  const queryParams = getUrlParamsFetch({ ...params });
  return get<ITicketResumen>(`${BASE_URL}tickets/resumen/?${queryParams}`, true);
};

// --- Export a Excel de la bandeja de tickets ---

export type ExportTicketsParams = Partial<GetGestionTicketsParams> & {
  // Opcional: si se pasa, ignora los demas filtros y exporta solo los
  // tickets cuyo UUID este en la lista (caso "exportar seleccion").
  ids?: string[];
};

/**
 * Llama GET /tickets/export con los filtros aplicados y descarga el XLSX
 * directamente al navegador. axios directo (no el wrapper) para poder
 * recibir `responseType: 'blob'`.
 */
export const exportarTicketsExcel = async (params: ExportTicketsParams) => {
  const token = useAuthStore.getState().token;
  const cleanParams: Record<string, any> = {};
  Object.entries(params || {}).forEach(([k, v]) => {
    if (v === undefined || v === null || v === '') return;
    if (Array.isArray(v) && v.length === 0) return;
    cleanParams[k] = Array.isArray(v) ? v.join(',') : v;
  });
  const resp = await axios.get(`${BASE_URL}tickets/export`, {
    params: cleanParams,
    responseType: 'blob',
    headers: { Authorization: 'Token ' + token },
    timeout: 600000,
  });

  // Trigger descarga via anchor invisible
  const blob = new Blob([resp.data], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  const fecha = new Date().toISOString().slice(0, 10);
  a.download = `tickets_${fecha}.xlsx`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
  return resp;
};

export const useExportarTicketsExcel = () => {
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: exportarTicketsExcel,
    onMutate: () => { setIsGlobalLoading(true); },
    onSuccess: () => {
      setAlert && setAlert(true, 'Tickets exportados a Excel', AlertSeverityEnum.success);
    },
    onError: error => { handleAxiosError(error); },
    onSettled: () => { setIsGlobalLoading(false); },
  });
};
//delete
export const eliminarTicket = (id:string)=>{
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  setIsGlobalLoading(true)
  return remove<ITicket>(`${BASE_URL}tickets/${id}/`, true)
}
export const useEliminarTicket=()=>{
  const queryClient = useQueryClient();
  //const setAlert = useUiStore.getState().setAlert;
  return useMutation({
    mutationFn: eliminarTicket,
    onSuccess:()=> {
      queryClient.refetchQueries({
        queryKey:['gestion-tickets'],
        type:'active'
      })
    },
    onError:error => {handleAxiosError(error)},
    onSettled:()=>{useUiStore.getState().setIsGlobalLoading(false)}
  })
}